import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import studentsAPI from "../../../services/students";
import classesAPI from "../../../services/classes";
import trainingsAPI from "../../../services/trainings";
import { useGetList as useGetScheduleList } from "../../../services/schedule";

const PrintAllClassesPage = () => {
  const { schoolId, trainingId } = useParams();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthContext();
  const [training, setTraining] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get slot filter from URL parameters
  const selectedSlotId = searchParams.get('slot_id') || "";

  // Get classes for this training with slot filter
  const classesQuery = classesAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    { slot_id: selectedSlotId }
  );

  // Get training data
  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, accessToken);
  const trainingData = trainingQuery?.data?.data;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch training data
        const trainingResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/schools/${schoolId}/trainings/${trainingId}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );
        
        if (trainingResponse.ok) {
          const trainingData = await trainingResponse.json();
          setTraining(trainingData?.data);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolId, trainingId, accessToken]);

  useEffect(() => {
    if (classesQuery.data?.data) {
      setClasses(classesQuery.data.data);
    }
  }, [classesQuery.data]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Handle DD/MM/YYYY format
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        // DD/MM/YYYY -> MM/DD/YYYY for JavaScript Date constructor
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('fr-FR');
      }
    }
    
    // Fallback for other date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fr-FR');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  };

  if (loading || classesQuery.isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Chargement...
      </div>
    );
  }


  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif',
      margin: 0,
      padding: '20px',
      backgroundColor: 'white',
      minHeight: '100vh',
      background: 'white'
    }}>
      <style>
        {`
          html, body {
            background: white !important;
            background-color: white !important;
          }
          
          @media print {
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            html, body {
              margin: 0;
              padding: 0;
              background: white !important;
              background-color: white !important;
            }
            .page-break {
              page-break-before: always;
            }
            .no-print {
              display: none;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .students-table tr:nth-child(even) {
              background-color: #f9f9f9 !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #e0e0e0 !important;
              border-bottom: 1px solid #e0e0e0 !important;
            }
            .schedule-table tr:nth-child(even) {
              background-color: #f8f9fa !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #e0e0e0 !important;
              border-bottom: 1px solid #e0e0e0 !important;
            }
            .students-table tr:nth-child(odd) {
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #f0f0f0 !important;
              border-bottom: 1px solid #f0f0f0 !important;
            }
            .schedule-table tr:nth-child(odd) {
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #f0f0f0 !important;
              border-bottom: 1px solid #f0f0f0 !important;
            }
          }
          
          .students-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0;
            font-size: 12px;
          }
          
          .students-table th,
          .students-table td {
            border: 1px solid #333;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }
          
          .students-table th {
            background-color: #f5f5f5;
            font-weight: bold;
            text-align: center;
          }
          
          .students-table tr:nth-child(even) {
            background-color: #f9f9f9;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
            border-top: 1px solid #e0e0e0;
            border-bottom: 1px solid #e0e0e0;
          }
          
          .students-table tr:nth-child(odd) {
            border-top: 1px solid #f0f0f0;
            border-bottom: 1px solid #f0f0f0;
          }
          
          .schedule-table tr:nth-child(even) {
            background-color: #f8f9fa;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
            border-top: 1px solid #e0e0e0;
            border-bottom: 1px solid #e0e0e0;
          }
          
          .schedule-table tr:nth-child(odd) {
            border-top: 1px solid #f0f0f0;
            border-bottom: 1px solid #f0f0f0;
          }
          
          .parent-info {
            min-width: 150px;
          }
          
          .contact-info {
            min-width: 120px;
          }
          
          .print-button {
            position: fixed;
            top: 90px;
            right: 20px;
            background-color: #007bff;
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 20px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
            transition: all 0.3s ease;
          }
          
          .print-button:hover {
            background-color: #0056b3;
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3);
          }
          
          .schedule-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 12px;
          }
          
          .schedule-table th,
          .schedule-table td {
            border: 1px solid #333;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }
          
          .schedule-table th {
            background-color: #e9ecef;
            font-weight: bold;
            text-align: center;
          }
          
          .schedule-table tr:nth-child(even) {
            background-color: #f8f9fa;
          }

          .class-header {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 20px;
            padding: 10px;
            background-color: #f8f9fa;
            border: 2px solid #333;
            text-align: center;
          }
        `}
      </style>

      <button 
        className="print-button no-print" 
        onClick={() => window.print()}
      >
        <i className="pi pi-print" style={{ marginRight: '8px' }}></i>
        Imprimer
      </button>

      {classes.length > 0 ? (
        classes.map((classe, classIndex) => (
          <ClassPrintSection
            key={classe.id}
            classe={classe}
            schoolId={schoolId}
            trainingId={trainingId}
            accessToken={accessToken}
            training={training}
            formatDate={formatDate}
            formatPhone={formatPhone}
            isFirst={classIndex === 0}
          />
        ))
      ) : (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>
            {selectedSlotId 
              ? "Aucune classe trouvée pour ce créneau" 
              : "Aucune classe trouvée dans cette formation"
            }
          </h2>
        </div>
      )}
    </div>
  );
};

// Separate component for each class print section
const ClassPrintSection = ({ 
  classe, 
  schoolId, 
  trainingId, 
  accessToken, 
  training, 
  formatDate, 
  formatPhone,
  isFirst
}) => {
  const [students, setStudents] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use the schedule hook with class filter
  const { data: scheduleQueryData, isLoading: scheduleLoading } = useGetScheduleList(
    schoolId,
    trainingId,
    accessToken,
    1,
    50,
    { classe_id: classe.id }
  );

  useEffect(() => {
    const fetchClassData = async () => {
      try {
        // Fetch students for this class
        const studentsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/schools/${schoolId}/trainings/${trainingId}/students?page=1&pageSize=1000&class_id=${classe.id}&status=ENROLLED`;
        
        const studentsResponse = await fetch(studentsUrl, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        });
        
        if (studentsResponse.ok) {
          const studentsData = await studentsResponse.json();
          const allStudents = studentsData?.data || [];
          setStudents(allStudents);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching class data:", error);
        setLoading(false);
      }
    };

    fetchClassData();
  }, [schoolId, trainingId, classe.id, accessToken]);

  useEffect(() => {
    if (scheduleQueryData?.data) {
      setScheduleData(scheduleQueryData.data);
    }
  }, [scheduleQueryData]);



  if (loading || scheduleLoading) {
    return null; // Don't render anything while loading
  }

  return (
    <div className={!isFirst ? "page-break" : ""}>
      <div className="class-header">
        Liste des élèves de la classe "{classe.name}" ({students.length} élève{students.length > 1 ? 's' : ''})
      </div>

      {/* Schedule Table */}
      {scheduleData && scheduleData.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <table className="schedule-table">
            <thead>
              <tr>
                <th style={{ width: '33.33%' }}>Créneau</th>
                <th style={{ width: '33.33%' }}>Enseignant(e)</th>
                <th style={{ width: '33.34%' }}>Salle</th>
              </tr>
            </thead>
            <tbody>
              {scheduleData.map((item, index) => (
                <tr key={index}>
                  <td>{item.slot?.name || item.slot_name || '-'}</td>
                  <td>{item.teacher?.member?.name || item.member?.lastname + ' ' + item.member?.firstname || '-'}</td>
                  <td>{item.classroom?.name || item.classroom_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <table className="students-table">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>N°</th>
            <th style={{ width: '15%' }}>Nom de l'élève</th>
            <th style={{ width: '10%' }}>Date de naissance</th>
            <th style={{ width: '8%' }}>Sexe</th>
            <th style={{ width: '15%' }}>Père</th>
            <th style={{ width: '10%' }}>Tél. Père</th>
            <th style={{ width: '15%' }}>Email Père</th>
            <th style={{ width: '15%' }}>Mère</th>
            <th style={{ width: '10%' }}>Tél. Mère</th>
            <th style={{ width: '15%' }}>Email Mère</th>
          </tr>
        </thead>
        <tbody>
          {students.length > 0 ? (
            students.map((student, index) => (
              <tr key={student.id || index}>
                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                  {index + 1}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <strong>{(student?.lastname || student?.member?.lastname || '')}</strong> {(student?.firstname || student?.member?.firstname || '')}
                </td>
                <td>{student?.birthday || student?.member?.birthday}</td>
                <td>{student?.gender === 'B' ? 'Garçon' : student?.gender === 'G' ? 'Fille' : '-'}</td>
                <td className="parent-info">
                  {(student?.father_lastname || '') + ' ' + (student?.father_firstname || '') || '-'}
                </td>
                <td className="contact-info">
                  {formatPhone(student?.father_phone)}
                </td>
                <td className="contact-info">
                  {student?.father_email || '-'}
                </td>
                <td className="parent-info">
                  {(student?.mother_lastname || '') + ' ' + (student?.mother_firstname || '') || '-'}
                </td>
                <td className="contact-info">
                  {formatPhone(student?.mother_phone)}
                </td>
                <td className="contact-info">
                  {student?.mother_email || '-'}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                Aucun élève trouvé dans cette classe
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ 
        marginTop: '30px', 
        textAlign: 'center', 
        fontSize: '12px', 
        color: '#666',
        lineHeight: '1.2'
      }}>
        <p style={{ margin: '2px 0', lineHeight: '1.2' }}>Formation: {training?.name || 'Non définie'}</p>
        <p style={{ margin: '2px 0', lineHeight: '1.2' }}>Période: {training?.start_courses_date ? formatDate(training.start_courses_date) : '-'} - {training?.end_courses_date ? formatDate(training.end_courses_date) : '-'}</p>
        <p style={{ margin: '2px 0', lineHeight: '1.2' }}>Généré le: {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );
};

export default PrintAllClassesPage;
